import { useMemo } from 'react';
import { Tools } from 'iconoir-react';
import { Bot, Folder, Layers3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AgentAttachedTool } from 'features/chat/agent-chat-module/types/agent.service.type';
import { useGetComposioToolkitCatalog } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { cn } from 'lib/utils';
import {
  AGENT_CARD_MAX_PROVIDER_ICONS,
  getAgentCardProviderIcons,
  normalizeProviderSlug,
} from '../utils/agent-tool-provider-icons';

type AgentCardProviderIconsProps = {
  tools?: AgentAttachedTool[] | null;
  enableTools?: boolean;
  hasKnowledge?: boolean;
  hasSkills?: boolean;
  hasSubagents?: boolean;
  knowledgeCount?: number;
  skillCount?: number;
  subagentCount?: number;
};

const capabilityIconClass =
  'flex size-6 shrink-0 items-center justify-center rounded-full border';

export function AgentCardProviderIcons({
  tools,
  enableTools = false,
  hasKnowledge = false,
  hasSkills = false,
  hasSubagents = false,
  knowledgeCount,
  skillCount,
  subagentCount,
}: AgentCardProviderIconsProps) {
  const { t } = useTranslation();
  const hasTools = (tools?.length ?? 0) > 0;
  const hasOtherCapabilities = hasKnowledge || hasSkills || hasSubagents;
  const { data: catalog } = useGetComposioToolkitCatalog({ enabled: hasTools });
  const catalogLogos = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of catalog?.toolkits ?? []) {
      if (!entry.logo) continue;
      for (const raw of [entry.slug, entry.name]) {
        const slug = normalizeProviderSlug(raw);
        if (!slug) continue;
        map.set(slug, entry.logo);
        map.set(slug.replace(/_/g, ''), entry.logo);
      }
    }
    return map;
  }, [catalog?.toolkits]);

  const icons = getAgentCardProviderIcons(tools, catalogLogos);
  const providerIcons = icons.filter((icon) => icon.src);
  const hasProviderTools = providerIcons.length > 0;

  if (!hasTools && !hasOtherCapabilities) return null;

  if (hasProviderTools) {
    const visible = providerIcons.slice(0, AGENT_CARD_MAX_PROVIDER_ICONS);
    const overflow = providerIcons.length - visible.length;
    const connectedLabel = enableTools
      ? t('CONNECTED', { defaultValue: 'Connected' })
      : t('NOT_CONNECTED', { defaultValue: 'Not Connected' });
    const statusLabel = overflow > 0 ? `+${overflow} ${connectedLabel}` : connectedLabel;

    return (
      <div className="flex min-w-0 flex-1 items-center gap-1.5" aria-label={statusLabel}>
        {visible.map((icon) => (
          <span
            key={icon.slug}
            className="flex size-5 shrink-0 items-center justify-center"
            title={icon.label}
          >
            <img src={icon.src!} alt={icon.label} className="size-full object-contain" />
          </span>
        ))}
        <span className="min-w-0 truncate text-xs font-normal text-grey-500 dark:text-muted-foreground">
          {statusLabel}
        </span>
      </div>
    );
  }

  if (hasOtherCapabilities) {
    const toolsLabel = t('TOOLS', { defaultValue: 'Tools' });
    const kbLabel = t('KNOWLEDGE_BASE', { defaultValue: 'Knowledge Base' });
    const skillsLabel = t('SKILLS', { defaultValue: 'Skills' });
    const subagentsLabel = t('SUBAGENTS', { defaultValue: 'Subagents' });

    return (
      <div className="flex min-w-0 flex-1 items-center gap-2" aria-label="Agent capabilities">
        {hasTools ? (
          <span className="flex shrink-0 items-center gap-0.5" title={toolsLabel}>
            <span
              className={cn(
                capabilityIconClass,
                'border-amber-200 bg-amber-50 text-amber-600 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-400'
              )}
            >
              <Tools className="size-3.5" strokeWidth={1.75} />
            </span>
          </span>
        ) : null}
        {hasKnowledge ? (
          <span
            className="flex shrink-0 items-center gap-0.5"
            title={knowledgeCount != null ? `${knowledgeCount} ${kbLabel}` : kbLabel}
          >
            <span
              className={cn(
                capabilityIconClass,
                'border-sky-200 bg-sky-50 text-sky-600 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-400'
              )}
            >
              <Folder className="size-3.5" strokeWidth={1.75} />
            </span>
            {knowledgeCount != null ? (
              <span className="text-xs font-normal text-grey-500 dark:text-muted-foreground">
                {knowledgeCount}
              </span>
            ) : null}
          </span>
        ) : null}
        {hasSkills ? (
          <span
            className="flex shrink-0 items-center gap-0.5"
            title={skillCount != null ? `${skillCount} ${skillsLabel}` : skillsLabel}
          >
            <span
              className={cn(
                capabilityIconClass,
                'border-violet-200 bg-violet-50 text-violet-600 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-400'
              )}
            >
              <Layers3 className="size-3.5" strokeWidth={1.75} />
            </span>
            {skillCount != null ? (
              <span className="text-xs font-normal text-grey-500 dark:text-muted-foreground">
                {skillCount}
              </span>
            ) : null}
          </span>
        ) : null}
        {hasSubagents ? (
          <span
            className="flex shrink-0 items-center gap-0.5"
            title={subagentCount != null ? `${subagentCount} ${subagentsLabel}` : subagentsLabel}
          >
            <span
              className={cn(
                capabilityIconClass,
                'border-teal-200 bg-teal-50 text-teal-600 dark:border-teal-800/60 dark:bg-teal-950/40 dark:text-teal-400'
              )}
            >
              <Bot className="size-3.5" strokeWidth={1.75} />
            </span>
            {subagentCount != null ? (
              <span className="text-xs font-normal text-grey-500 dark:text-muted-foreground">
                {subagentCount}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
    );
  }

  if (icons.length === 0) return null;

  const toolCount = tools?.length ?? icons.length;
  const statusLabel = t(enableTools ? 'N_TOOLS_CONNECTED' : 'N_TOOLS_NOT_CONNECTED', {
    count: toolCount,
    defaultValue: `${toolCount} tools ${enableTools ? 'connected' : 'not connected'}`,
  });

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1.5" aria-label={statusLabel}>
      <span className="min-w-0 truncate text-xs font-normal text-grey-500 dark:text-muted-foreground">
        {statusLabel}
      </span>
    </div>
  );
}
